<template>
    <div>
        <div v-if="currentUserIsOrganizationAdmin && currentUserOrganizationUnitId == organizationUnitId" id="navigationPlaceholder" style="padding-top: 80px;">
            <AdminNavigationV2 v-if="organizationUnitId" :tid="organizationUnitId"></AdminNavigationV2>
        </div>

        <div class="container">
            <div class="row">
                <div class="col-lg-7">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b;">
                        {{ isServiceUser ? "Manage service account's roles" : "Manage user's roles" }}
                    </h1>

                    <button @click="$router.go(-1)" class="btn btn-link btn-sm">
                        <i class="fa fa-arrow-left" aria-hidden="true"></i>
                        Back
                    </button>
                </div>
            </div>
        </div>

        <hr style="border-color: #bbb; margin: 20px 0 30px;" />

        <div v-if="id && user" class="container">
            <h4 class="fg-color-blue">
                {{displayName}} is currently {{ roles.join(', ') }}
                <span v-if="isServiceUser" class="badge badge-info ml-2">Service Account</span>
            </h4>

            <!-- Informational alert for service accounts -->
            <div v-if="isServiceUser" class="alert alert-info mt-3" role="alert">
                <i class="fa fa-info-circle" aria-hidden="true"></i>
                <strong>Service Account Roles:</strong> These roles determine the API access permissions for this service account. The roles control what actions the service account can perform through the API.
            </div>

            <br />
            <div class="row">
                <div v-if="currentUserIsSystemAdmin || currentUserIsOrganizationAdmin" class="col-sm-6 mb-4">
                    <div class="card text-white" :class="[isBusinessUser ? 'bg-primary' : 'bg-secondary']">
                        <div class="card-header">Business User</div>
                        <div class="card-body">
                            <div class="card-text" style="height: 170px;">
                                <span v-if="isServiceUser">Service accounts in this role have API access to:</span>
                                <span v-else>Users in this role have an access to individual dashboards and shared assets. They permissions include:</span>
                                <ul>
                                    <li v-if="!isServiceUser">Sign in to transparentChoice</li>
                                    <li>Access shared assets</li>
                                    <li v-if="!isServiceUser">Use dashboard</li>
                                    <li v-if="isServiceUser">Read-only API operations</li>
                                </ul>
                            </div>
                        </div>
                        <div class="card-footer text-center">
                            <button v-if="!isBusinessUser" @click="handleAssignBusinessUserRoleCommandV1" type="button" class="btn btn-light text-ellipsis">Assign Business User</button>
                            <button v-else @click="handleRevokeBusinessUserRoleCommandV1" type="button" class="btn btn-light text-ellipsis">Revoke Business User</button>
                        </div>
                    </div>
                </div>

                <div v-if="currentUserIsSystemAdmin || currentUserIsOrganizationAdmin" class="col-sm-6 mb-4">
                    <div class="card text-white" :class="[isWorkspaceUser ? 'bg-primary' : 'bg-secondary']">
                        <div class="card-header">Workspace User</div>
                        <div class="card-body">
                            <div class="card-text" style="height: 170px;">
                                <span v-if="isServiceUser">Service accounts in this role have API access to:</span>
                                <span v-else>Users in this role have an access to workspaces and its portfolios. They permissions include:</span>
                                <ul>
                                    <li>Creating a new portfolios</li>
                                    <li>Managing projects</li>
                                    <li>Managing criteria</li>
                                    <li v-if="!isServiceUser">Managing surveys</li>
                                    <li v-if="!isServiceUser">Browsing results</li>
                                    <li v-if="isServiceUser">Full workspace API operations</li>
                                </ul>
                            </div>
                        </div>
                        <div class="card-footer text-center">
                            <button v-if="!isWorkspaceUser" @click="handleAssignWorkspaceUserRoleCommandV1" type="button" class="btn btn-light text-ellipsis">Assign Workspace User</button>
                            <button v-else @click="handleRevokeWorkspaceUserRoleCommandV1" type="button" class="btn btn-light text-ellipsis">Revoke Workspace User</button>
                        </div>
                    </div>
                </div>

                <div v-if="currentUserIsSystemAdmin || currentUserIsGlobalAdmin || currentUserIsOrganizationAdmin" class="col-sm-6 mb-4">
                    <div class="card text-white" :class="[isOrganizationAdmin ? 'bg-primary' : 'bg-secondary']">
                        <div class="card-header">Organization Admin</div>
                        <div class="card-body">
                            <div class="card-text" style="height: 170px;">
                                <span v-if="isServiceUser">Service accounts in this role have API access to:</span>
                                <span v-else>Users in this role have an access to admin panel and can manage within a single organizations. They permissions include:</span>
                                <ul>
                                    <li>Managing users</li>
                                    <li>Managing workspaces</li>
                                    <li>Importing portfolios</li>
                                    <li v-if="isServiceUser">Organization-level API operations</li>
                                </ul>
                            </div>
                        </div>
                        <div class="card-footer text-center">
                            <button v-if="!isOrganizationAdmin" @click="handleAssignOrganizationAdminRoleCommandV1" type="button" class="btn btn-light text-ellipsis">Assign Organization Admin</button>
                            <button v-else @click="handleRevokeOrganizationAdminRoleCommandV1" type="button" class="btn btn-light text-ellipsis">Revoke Organization Admin</button>
                        </div>
                    </div>
                </div>

                <div v-if="currentUserIsSystemAdmin || currentUserIsGlobalAdmin" class="col-sm-6 mb-4">
                    <div class="card text-white" :class="[isGlobalAdmin ? 'bg-primary' : 'bg-secondary']">
                        <div class="card-header">Global Admin</div>
                        <div class="card-body">
                            <div class="card-text" style="height: 170px;">
                                <span v-if="isServiceUser">Service accounts in this role have API access to:</span>
                                <span v-else>Users in this role have an access to admin panel and can set up organizations. They permissions include:</span>
                                <ul>
                                    <li>Setting up a new organizations</li>
                                    <li>Assign global admins</li>
                                    <li>Assign organization admins</li>
                                    <li v-if="isServiceUser">System-wide API operations</li>
                                </ul>
                            </div>
                        </div>
                        <div class="card-footer text-center">
                            <button v-if="!isGlobalAdmin" @click="handleAssignGlobalAdminRoleCommandV1" type="button" class="btn btn-light text-ellipsis">Assign Global Admin</button>
                            <button v-else @click="handleRevokeGlobalAdminRoleCommandV1" type="button" class="btn btn-light text-ellipsis">Revoke Global Admin</button>
                        </div>
                    </div>
                </div>

            </div>
        </div>

        <div v-else-if="!id" class="container">
            <div class="input-group mb-3">
                <input v-model="emailinput" type="email" class="form-control" placeholder="Find user by email">
                <div class="input-group-append">
                    <button @click="handleFindUser" class="btn btn-outline-secondary" type="button">Find</button>
                </div>
            </div>
        </div>

        <div v-else class="container">
            <h4>Email '{{id}}' not found</h4>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ref, computed, watch, onMounted } from 'vue'
    import { useRoute, useRouter } from 'vue-router'

    import AdminNavigationV2 from '@common/AdminNavigationV2.vue'

    import
        {
            useCurrentUserQueryV1,
            useUserQueryV1
        } from '@Organizations/Users/queries-vue-composables'

    import
        {
            useAssignGlobalAdminRoleCommandV1,
            useRevokeGlobalAdminRoleCommandV1,
            useAssignOrganizationAdminRoleCommandV1,
            useRevokeOrganizationAdminRoleCommandV1,
            useAssignWorkspaceUserRoleCommandV1,
            useRevokeWorkspaceUserRoleCommandV1,
            useAssignBusinessUserRoleCommandV1,
            useRevokeBusinessUserRoleCommandV1
        } from '@Organizations/Users/commands-vue-composables'

    // ---------------- route ----------------

    const route = useRoute()
    const router = useRouter()

    const id = computed(() =>
        typeof route.params.id === 'string'
            ? route.params.id
            : null
    )

    // ---------------- local state ----------------

    const emailinput = ref('')

    // ---------------- queries ----------------

    // current (logged-in) user
    const {
        modelCurrentUserQueryV1: userSessionDetails,
        runCurrentUserQueryV1
    } = useCurrentUserQueryV1(false, false, true)

    // managed user (ID is bound here)
    const {
        modelUserQueryV1: user,
        runUserQueryV1
    } = useUserQueryV1(id)

    // ---------------- commands (ID bound in use) ----------------

    const {
        invokeAssignGlobalAdminRoleCommandV1,
        onSucceedAssignGlobalAdminRoleCommandV1
    } = useAssignGlobalAdminRoleCommandV1(id)

    const {
        invokeRevokeGlobalAdminRoleCommandV1,
        onSucceedRevokeGlobalAdminRoleCommandV1
    } = useRevokeGlobalAdminRoleCommandV1(id)

    const {
        invokeAssignOrganizationAdminRoleCommandV1,
        onSucceedAssignOrganizationAdminRoleCommandV1
    } = useAssignOrganizationAdminRoleCommandV1(id)

    const {
        invokeRevokeOrganizationAdminRoleCommandV1,
        onSucceedRevokeOrganizationAdminRoleCommandV1
    } = useRevokeOrganizationAdminRoleCommandV1(id)

    const {
        invokeAssignWorkspaceUserRoleCommandV1,
        onSucceedAssignWorkspaceUserRoleCommandV1
    } = useAssignWorkspaceUserRoleCommandV1(id)

    const {
        invokeRevokeWorkspaceUserRoleCommandV1,
        onSucceedRevokeWorkspaceUserRoleCommandV1
    } = useRevokeWorkspaceUserRoleCommandV1(id)

    const {
        invokeAssignBusinessUserRoleCommandV1,
        onSucceedAssignBusinessUserRoleCommandV1
    } = useAssignBusinessUserRoleCommandV1(id)

    const {
        invokeRevokeBusinessUserRoleCommandV1,
        onSucceedRevokeBusinessUserRoleCommandV1
    } = useRevokeBusinessUserRoleCommandV1(id)

    // ---------------- lifecycle ----------------

    onMounted(() =>
    {
        document.title = "Manage user's roles"
        runCurrentUserQueryV1()
    })

    // ---------------- react to route param ----------------

    watch(id, (newId) =>
    {
        if (newId)
            runUserQueryV1()
    }, { immediate: true })

    // ---------------- refresh after ANY role change ----------------

    function refreshUser()
    {
        if (id.value)
            runUserQueryV1()
    }

    onSucceedAssignGlobalAdminRoleCommandV1(refreshUser)
    onSucceedRevokeGlobalAdminRoleCommandV1(refreshUser)
    onSucceedAssignOrganizationAdminRoleCommandV1(refreshUser)
    onSucceedRevokeOrganizationAdminRoleCommandV1(refreshUser)
    onSucceedAssignWorkspaceUserRoleCommandV1(refreshUser)
    onSucceedRevokeWorkspaceUserRoleCommandV1(refreshUser)
    onSucceedAssignBusinessUserRoleCommandV1(refreshUser)
    onSucceedRevokeBusinessUserRoleCommandV1(refreshUser)

    // ---------------- derived: current user ----------------

    const currentUserOrganizationUnitId = computed(() =>
        userSessionDetails.value?.OrganizationUnitId
    )

    const currentUserIsSystemAdmin = computed(() =>
        userSessionDetails.value?.IsSystemAdmin ?? false
    )

    const currentUserIsGlobalAdmin = computed(() =>
        userSessionDetails.value?.IsGlobalAdmin ?? false
    )

    const currentUserIsOrganizationAdmin = computed(() =>
        userSessionDetails.value?.IsOrganizationAdmin ?? false
    )

    // ---------------- derived: managed user ----------------

    const email = computed(() => user.value?.Email)
    const displayName = computed(() => user.value?.DisplayName)

    const isSystemAdmin = computed(() => user.value?.IsSystemAdmin)
    const isGlobalAdmin = computed(() => user.value?.IsGlobalAdmin)
    const isOrganizationAdmin = computed(() => user.value?.IsOrganizationAdmin)
    const isWorkspaceUser = computed(() => user.value?.IsWorkspaceUser)
    const isBusinessUser = computed(() => user.value?.IsBusinessUser)
    const isServiceUser = computed(() => user.value?.IsServiceUser ?? false)

    const organizationUnitId = computed(() =>
        user.value?.OrganizationUnitId
    )

    // ---------------- derived: roles label ----------------

    const roles = computed(() =>
    {
        const r: string[] = []

        if (isSystemAdmin.value) r.push('System Admin')
        if (isGlobalAdmin.value) r.push('Global Admin')
        if (isOrganizationAdmin.value) r.push('Organization Admin')
        if (isWorkspaceUser.value) r.push('Workspace User')
        if (isBusinessUser.value) r.push('Business User')
        if (isServiceUser.value) r.push('Service Account')

        return r
    })

    // ---------------- helpers ----------------

    function confirmAction(action: string)
    {
        const accountType = isServiceUser.value ? 'service account' : 'user'
        return confirm(`Do you really want to ${action} for ${accountType} ${email.value}?`)
    }

    // ---------------- handlers ----------------

    function handleAssignGlobalAdminRoleCommandV1()
    {
        if (confirmAction('assign Global Admin role'))
            invokeAssignGlobalAdminRoleCommandV1()
    }

    function handleRevokeGlobalAdminRoleCommandV1()
    {
        if (confirmAction('revoke Global Admin role'))
            invokeRevokeGlobalAdminRoleCommandV1()
    }

    function handleAssignOrganizationAdminRoleCommandV1()
    {
        if (confirmAction('assign Organization Admin role'))
            invokeAssignOrganizationAdminRoleCommandV1()
    }

    function handleRevokeOrganizationAdminRoleCommandV1()
    {
        if (confirmAction('revoke Organization Admin role'))
            invokeRevokeOrganizationAdminRoleCommandV1()
    }

    function handleAssignWorkspaceUserRoleCommandV1()
    {
        if (confirmAction('assign Workspace User role'))
            invokeAssignWorkspaceUserRoleCommandV1()
    }

    function handleRevokeWorkspaceUserRoleCommandV1()
    {
        if (confirmAction('revoke Workspace User role'))
            invokeRevokeWorkspaceUserRoleCommandV1()
    }

    function handleAssignBusinessUserRoleCommandV1()
    {
        if (confirmAction('assign Business User role'))
            invokeAssignBusinessUserRoleCommandV1()
    }

    function handleRevokeBusinessUserRoleCommandV1()
    {
        if (confirmAction('revoke Business User role'))
            invokeRevokeBusinessUserRoleCommandV1()
    }

    function handleFindUser()
    {
        if (!emailinput.value) return

        router.push(
            `/admin/organizations/users/manage/roles/${encodeURIComponent(emailinput.value)}`
        )
    }
</script>
